import React, { useState } from 'react';
import styled from 'styled-components';
import SheetListPanel from './SheetListPanel.jsx';
import SheetPagesPanel from './SheetPagesPanel.jsx';
import ScaleReadoutPanel from './ScaleReadoutPanel.jsx';
import UnitsSettingsPanel from './UnitsSettingsPanel.jsx';
import { sheetDisplayName } from './sheetPanelUtils.js';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f8f9fa;
`;

const PanelHeader = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  flex-shrink: 0;
`;

const Sub = styled.div`
  font-size: 11px;
  color: #6b7280;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const FileSelect = styled.select`
  flex: 1;
  min-width: 0;
  height: 28px;
  padding: 0 6px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 11px;
  background: #fff;
`;

const IndexingBanner = styled.div`
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  background: rgba(233, 112, 36, 0.1);
  color: #9a3412;
`;

const EmptyPanel = styled.div`
  padding: 20px 16px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.6;
`;

const LinkBtn = styled.button`
  display: block;
  margin-top: 12px;
  padding: 8px 14px;
  border: 1px solid rgba(233, 112, 36, 0.3);
  border-radius: 8px;
  background: rgba(233, 112, 36, 0.08);
  color: #e97024;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
`;

export default function LeftSidebarTabs({
  sheets,
  sheetsLoading,
  activeIrKey,
  activeSheet,
  activePage,
  pdfUrl,
  pdfDoc,
  drawingFolders,
  activeRequirementId,
  onFolderChange,
  onSelectSheet,
  onSelectPage,
  onNavigateToLinkFolders,
  hasIndexingInProgress,
  entityPages,
  viewportInfo,
  projectId,
  requirementId,
  scaleOverride,
  onScaleOverrideChange,
}) {
  const [sheetSearch, setSheetSearch] = useState('');

  const indexingCount = sheets.filter(
    (s) => (s.extractionStatus || s.ir?.extractionStatus) === 'extracting'
  ).length;

  const singleSheet = sheets.length === 1;
  const showFolderSelect = drawingFolders?.length > 1 && onFolderChange;

  return (
    <Root>
      <PanelHeader>
        <HeaderRow>
          <Title>Sheets</Title>
          {showFolderSelect ? (
            <FileSelect value={activeRequirementId || ''} onChange={onFolderChange}>
              {drawingFolders.map((f) => (
                <option key={f.requirementId} value={f.requirementId}>
                  {f.title || f.requirementId}
                </option>
              ))}
            </FileSelect>
          ) : activeSheet ? (
            <Sub title={sheetDisplayName(activeSheet)}>
              {sheetDisplayName(activeSheet)}
            </Sub>
          ) : (
            <Sub>Select a sheet</Sub>
          )}
        </HeaderRow>
        {hasIndexingInProgress && indexingCount > 0 && (
          <IndexingBanner>
            Indexing {indexingCount} sheet{indexingCount === 1 ? '' : 's'}…
          </IndexingBanner>
        )}
      </PanelHeader>

      {!sheetsLoading && sheets.length === 0 ? (
        <EmptyPanel>
          No drawing sheets yet.
          {onNavigateToLinkFolders && (
            <LinkBtn type="button" onClick={onNavigateToLinkFolders}>
              Go to Link Folders
            </LinkBtn>
          )}
        </EmptyPanel>
      ) : (
        <>
          <SheetListPanel
            sheets={sheets}
            sheetsLoading={sheetsLoading}
            activeIrKey={activeIrKey}
            search={sheetSearch}
            onSearchChange={setSheetSearch}
            onSelectSheet={onSelectSheet}
            hideWhenSingle={singleSheet}
          />
          <SheetPagesPanel
            activeSheet={activeSheet}
            activePage={activePage}
            pdfUrl={pdfUrl}
            pdfDoc={pdfDoc}
            onSelectPage={onSelectPage}
          />
          <ScaleReadoutPanel
            activeSheet={activeSheet}
            activePage={activePage}
            entityPages={entityPages}
            viewportInfo={viewportInfo}
            projectId={projectId}
            requirementId={requirementId}
            scaleOverride={scaleOverride}
            onScaleOverrideChange={onScaleOverrideChange}
          />
          <UnitsSettingsPanel projectId={projectId} />
        </>
      )}
    </Root>
  );
}
